import { useState } from 'react'
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl'
import { keepPreviousData, useQueryClient } from '@tanstack/react-query'
import { useStore } from 'zustand'

import { ClockIcon } from '@gto/icons-react/outline/clock'
import { ShieldCheckIcon } from '@gto/icons-react/outline/shield-check'
import { UserPlusIcon } from '@gto/icons-react/outline/user-plus'
import { Alert } from '@gto/ui/alert'
import { Button } from '@gto/ui/button'
import type { ColumnDef, PaginationState } from '@gto/ui/data-table'
import { createColumns } from '@gto/ui/data-table'
import { Typography } from '@gto/ui/typography'

import type {
  AffiliateAdminAccessChangeSchema,
  AffiliateAdminAccessGrantSchema,
  AffiliateAdminAccessSchema,
} from '@/api/gen'
import {
  getListAdminAccessChangesQueryKey,
  getListAdminAccessQueryKey,
  useGrantAdminAccessView,
  useListAdminAccess,
  useListAdminAccessChanges,
  useRevokeAdminAccessView,
} from '@/api/gen'
import { ConfirmDialog } from '@/components/confirm-dialog'
import { DataTable } from '@/components/data-table'
import { EmptyState } from '@/components/empty-state'
import { InlineQueryError } from '@/components/inline-query-error'
import { MutationError } from '@/components/mutation-error'
import { Page } from '@/components/page'
import { StatsCard } from '@/components/stats-card'
import { fixedColumn, fluidColumn } from '@/components/table-columns'
import { roleLabelId } from '@/domain/admin-access'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { store } from '@/store'
import { extractValidationDetail } from '@/utils/extract-validation-detail'

import { GrantAccessForm } from './grant-access-form'

const HISTORY_PAGE_SIZE = 25

type Notice = {
  messageId: 'adminAccess.grant.success' | 'adminAccess.revoke.success'
  name: string
}

const RoleLabel = ({ role }: { role: string }) => {
  const id = roleLabelId(role)

  return id === null ? role : <FormattedMessage id={id} />
}

// ─── Columns ────────────────────────────────────────────────────────────────

const useAdminColumns = (
  viewerId: string | undefined,
  onRevoke: (admin: AffiliateAdminAccessSchema) => void,
): ColumnDef<AffiliateAdminAccessSchema>[] => {
  const { formatMessage } = useIntl()

  return createColumns<AffiliateAdminAccessSchema>([
    {
      accessorKey: 'display_name',
      header: formatMessage({ id: 'adminAccess.col.name' }),
      ...fluidColumn({ min: 160, flex: 1.2 }),
      cell: ({ row }) => row.original.display_name,
    },
    {
      accessorKey: 'email',
      header: formatMessage({ id: 'adminAccess.col.email' }),
      ...fluidColumn({ min: 200, flex: 1.6 }),
      cell: ({ row }) => row.original.email,
    },
    {
      accessorKey: 'role',
      header: formatMessage({ id: 'adminAccess.col.role' }),
      ...fluidColumn({ min: 100, flex: 0.6 }),
      cell: ({ row }) => <RoleLabel role={row.original.role} />,
    },
    {
      id: 'actions',
      header: '',
      ...fixedColumn({ min: 120, align: 'right' }),
      enableSorting: false,
      enableHiding: false,
      // The backend refuses a revoke of your own access; the viewer's row offers
      // no control rather than one that can only fail.
      cell: ({ row }) =>
        row.original.id === viewerId ? (
          <Typography
            color="text.secondary"
            variant="body2"
          >
            <FormattedMessage id="adminAccess.admins.you" />
          </Typography>
        ) : (
          <Button
            color="error"
            data-tst="revoke-access"
            size="small"
            variant="text"
            onClick={() => onRevoke(row.original)}
          >
            <FormattedMessage id="adminAccess.revoke.action" />
          </Button>
        ),
    },
  ])
}

const useHistoryColumns = (): ColumnDef<AffiliateAdminAccessChangeSchema>[] => {
  const { formatMessage } = useIntl()

  return createColumns<AffiliateAdminAccessChangeSchema>([
    {
      accessorKey: 'created_at',
      header: formatMessage({ id: 'adminAccess.history.col.when' }),
      ...fluidColumn({ min: 160, flex: 0.9 }),
      enableSorting: false,
      cell: ({ row }) => (
        <FormattedDate
          dateStyle="medium"
          timeStyle="short"
          value={row.original.created_at}
        />
      ),
    },
    {
      id: 'actor',
      header: formatMessage({ id: 'adminAccess.history.col.actor' }),
      ...fluidColumn({ min: 180, flex: 1.2 }),
      enableSorting: false,
      cell: ({ row }) =>
        row.original.actor_email ?? (
          <Typography
            color="text.secondary"
            variant="body2"
          >
            <FormattedMessage id="adminAccess.history.deletedActor" />
          </Typography>
        ),
    },
    {
      id: 'account',
      header: formatMessage({ id: 'adminAccess.history.col.account' }),
      ...fluidColumn({ min: 200, flex: 1.4 }),
      enableSorting: false,
      cell: ({ row }) => (
        <div className="flex flex-col">
          <Typography variant="body2">{row.original.partner_display_name}</Typography>
          <Typography
            color="text.secondary"
            variant="caption"
          >
            {row.original.partner_email}
          </Typography>
        </div>
      ),
    },
    {
      id: 'change',
      header: formatMessage({ id: 'adminAccess.history.col.change' }),
      ...fluidColumn({ min: 160, flex: 0.9 }),
      enableSorting: false,
      cell: ({ row }) => (
        <FormattedMessage
          id="adminAccess.history.change"
          values={{
            from: <RoleLabel role={row.original.from_role} />,
            to: <RoleLabel role={row.original.to_role} />,
          }}
        />
      ),
    },
  ])
}

// ─── Page ───────────────────────────────────────────────────────────────────

export const AccessPage = () => {
  const { formatMessage } = useIntl()
  const queryClient = useQueryClient()
  const viewerId = useStore(store, (s) => s.partner?.id)
  const [revokeTarget, setRevokeTarget] = useState<AffiliateAdminAccessSchema | null>(null)
  const [notice, setNotice] = useState<Notice | null>(null)
  const [historyPagination, setHistoryPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: HISTORY_PAGE_SIZE,
  })

  const adminsQuery = useListAdminAccess()
  useReportQueryError(adminsQuery, 'useListAdminAccess failed: ')

  const historyQuery = useListAdminAccessChanges(
    { page: historyPagination.pageIndex + 1, page_size: historyPagination.pageSize },
    { query: { placeholderData: keepPreviousData } },
  )
  useReportQueryError(historyQuery, 'useListAdminAccessChanges failed: ')

  // Without params, so the prefix match refreshes every cached history page.
  const refreshAccess = () => {
    void queryClient.invalidateQueries({ queryKey: getListAdminAccessQueryKey() })
    void queryClient.invalidateQueries({ queryKey: getListAdminAccessChangesQueryKey() })
    setHistoryPagination((prev) => ({ ...prev, pageIndex: 0 }))
  }

  const grantError = useMutationErrorHandler('useGrantAdminAccessView failed: ', {
    extractDetail: extractValidationDetail,
  })
  const grantAccess = useGrantAdminAccessView({ mutation: { onError: grantError.onError } })

  const submitGrant = (body: AffiliateAdminAccessGrantSchema, onGranted: () => void) => {
    grantError.clearError()
    setNotice(null)
    grantAccess.mutate(
      { data: body },
      {
        onSuccess: (granted) => {
          refreshAccess()
          onGranted()
          setNotice({ messageId: 'adminAccess.grant.success', name: granted.display_name })
        },
      },
    )
  }

  const revokeError = useMutationErrorHandler('useRevokeAdminAccessView failed: ', {
    extractDetail: extractValidationDetail,
  })
  const revokeAccess = useRevokeAdminAccessView({
    mutation: {
      onSuccess: (revoked) => {
        refreshAccess()
        setRevokeTarget(null)
        setNotice({ messageId: 'adminAccess.revoke.success', name: revoked.display_name })
      },
      onError: revokeError.onError,
    },
  })

  const openRevoke = (admin: AffiliateAdminAccessSchema) => {
    revokeError.clearError()
    setRevokeTarget(admin)
  }

  const adminColumns = useAdminColumns(viewerId, openRevoke)
  const historyColumns = useHistoryColumns()

  const admins = adminsQuery.data ?? []
  const changes = historyQuery.data?.changes ?? []
  const historyTotal = historyQuery.data?.total ?? 0

  return (
    <Page>
      <Page.Header title={<FormattedMessage id="adminAccess.title" />} />

      <Page.Content>
        {notice !== null && (
          <Alert
            data-tst="access-notice"
            severity="success"
          >
            <FormattedMessage
              id={notice.messageId}
              values={{ name: notice.name }}
            />
          </Alert>
        )}

        <StatsCard
          icon={<UserPlusIcon />}
          title={<FormattedMessage id="adminAccess.grant.title" />}
        >
          <div className="flex flex-col gap-3">
            <MutationError
              data-tst="grant-access-error"
              detail={grantError.detail}
              messageId="adminAccess.grant.error"
              show={grantError.hasError}
            />
            <GrantAccessForm
              submitting={grantAccess.isPending}
              onSubmit={submitGrant}
            />
          </div>
        </StatsCard>

        <StatsCard
          icon={<ShieldCheckIcon />}
          title={<FormattedMessage id="adminAccess.admins.title" />}
        >
          {adminsQuery.isError ? (
            <InlineQueryError
              data-tst="admins-error"
              messageId="adminAccess.admins.error"
              onRetry={() => void adminsQuery.refetch()}
            />
          ) : !adminsQuery.isLoading && admins.length === 0 ? (
            <EmptyState title={<FormattedMessage id="adminAccess.admins.empty" />} />
          ) : (
            <DataTable<AffiliateAdminAccessSchema>
              aria-label={formatMessage({ id: 'adminAccess.admins.title' })}
              columns={adminColumns}
              data={admins}
              getRowId={(row) => row.id}
              loading={adminsQuery.isLoading}
            />
          )}
        </StatsCard>

        <StatsCard
          icon={<ClockIcon />}
          title={<FormattedMessage id="adminAccess.history.title" />}
        >
          {historyQuery.isError ? (
            <InlineQueryError
              data-tst="history-error"
              messageId="adminAccess.history.error"
              onRetry={() => void historyQuery.refetch()}
            />
          ) : !historyQuery.isLoading && historyTotal === 0 ? (
            <EmptyState title={<FormattedMessage id="adminAccess.history.empty" />} />
          ) : (
            <DataTable<AffiliateAdminAccessChangeSchema>
              manualPagination
              aria-label={formatMessage({ id: 'adminAccess.history.title' })}
              columns={historyColumns}
              data={changes}
              getRowId={(row) => row.id}
              loading={historyQuery.isLoading}
              pagination={historyPagination}
              refetching={historyQuery.isPlaceholderData}
              rowCount={historyTotal}
              onPaginationChange={setHistoryPagination}
            />
          )}
        </StatsCard>
      </Page.Content>

      {revokeTarget && (
        <ConfirmDialog
          open
          cancelLabel={<FormattedMessage id="admin.common.cancel" />}
          confirmColor="error"
          confirmLabel={<FormattedMessage id="adminAccess.revoke.confirm" />}
          loading={revokeAccess.isPending}
          title={<FormattedMessage id="adminAccess.revoke.title" />}
          description={formatMessage(
            { id: 'adminAccess.revoke.description' },
            { name: revokeTarget.display_name, email: revokeTarget.email },
          )}
          onCancel={() => setRevokeTarget(null)}
          onConfirm={() => revokeAccess.mutate({ partnerId: revokeTarget.id })}
        >
          <MutationError
            data-tst="revoke-access-error"
            detail={revokeError.detail}
            messageId="adminAccess.revoke.error"
            show={revokeError.hasError}
          />
        </ConfirmDialog>
      )}
    </Page>
  )
}
